import { useState } from "react";
import { useIntl } from "react-intl";
import { useMediaQuery } from "@wayflyer/flyui";
import { IconArrowLeft24Line, IconArrowRight24Line } from "@wayflyer/flyui-icons/24/line";

import { COMMON_MESSAGES } from "../../../components/common-messages";
import { DOCUMENT_NAMES, DOCUMENT_TEMPLATES } from "../../../components/incomplete-steps-modal/message-definitions";
import { PAGE_TYPE, useFundingApplicationNavigation } from "../../../hooks/navigation-next";
import { useApplicationSubmit } from "../../../hooks/use-application-submit";
import { UploadDocumentType } from "../../../types";

import { MESSAGES } from "./message-definitions";

type UseDocumentNavigationActionsOptions = {
  forType: string;
  documentTypes?: string[];
  onPrimaryAction?: () => void;
};

export const useDocumentNavigationActions = ({
  forType,
  documentTypes = [forType],
  onPrimaryAction,
}: UseDocumentNavigationActionsOptions) => {
  const intl = useIntl();
  const query = useMediaQuery();
  const isMobile = query("sm").windowLte;
  const navigationContext = useFundingApplicationNavigation({
    currentPage: PAGE_TYPE.DOCUMENTS,
    currentStep: forType,
  });
  const applicationSubmit = useApplicationSubmit({
    currentPage: PAGE_TYPE.DOCUMENTS,
    currentStep: forType,
  });
  const [isPrimarySubmitting, setIsPrimarySubmitting] = useState(false);
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  const isDocumentComplete = documentTypes.every(
    (type) => applicationSubmit.stageCompletionStatus.documents?.[type] ?? false
  );

  const documentErrors: Record<string, string | undefined> = {};
  documentTypes.forEach((type) => {
    const isTypeComplete = applicationSubmit.stageCompletionStatus.documents?.[type] ?? false;
    if (hasAttemptedSubmit && !isTypeComplete) {
      if (type === UploadDocumentType.POOL_BANCARIO) {
        documentErrors[type] = intl.formatMessage(MESSAGES.poolBancarioInlineError);
      } else if (type === UploadDocumentType.CIRBE) {
        documentErrors[type] = intl.formatMessage(MESSAGES.cirbeInlineError);
      } else {
        const nameDescriptor = DOCUMENT_NAMES[type as keyof typeof DOCUMENT_NAMES];
        documentErrors[type] = nameDescriptor
          ? intl.formatMessage(DOCUMENT_TEMPLATES.inlineError, { documentName: intl.formatMessage(nameDescriptor) })
          : intl.formatMessage(COMMON_MESSAGES.pleaseFillOutThisField);
      }
    }
  });
  // Convenience for single-document pages, equivalent to documentErrors[forType]. Multi-document
  // pages (e.g. financial-documents) should use documentErrors directly instead.
  // TODO: consolidate single-document pages onto documentErrors and drop this.
  const documentError = documentErrors[forType];

  const primaryAction = {
    content: navigationContext.nextButtonLabel,
    icon: IconArrowRight24Line,
    loading: isPrimarySubmitting || applicationSubmit.isSubmitting,
    analyticsId: "document-uploads-next-button",
    onAction: async () => {
      if (!navigationContext.nextPath) {
        if (!isDocumentComplete) {
          setHasAttemptedSubmit(true);
          return;
        }
        applicationSubmit.submitApplication();
        return;
      }
      setIsPrimarySubmitting(true);
      try {
        await navigationContext.navigateWithStateUpdate(navigationContext.nextPath);
      } finally {
        setIsPrimarySubmitting(false);
      }
      onPrimaryAction?.();
    },
  };

  const secondaryAction = {
    content: isMobile ? undefined : navigationContext.backButtonLabel,
    icon: IconArrowLeft24Line,
    analyticsId: "document-uploads-back-button",
    onAction: () => {
      if (navigationContext.previousPath) {
        navigationContext.navigate(navigationContext.previousPath);
      }
    },
  };

  return { primaryAction, secondaryAction, applicationSubmit, documentError, documentErrors };
};
